import { useMemo } from "react";
import { ArrowUpRight, FileText, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtUSD } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

const SPARKLINE_MARGIN = { top: 4, right: 4, bottom: 0, left: 4 };

const SAMPLE_MONTHLY = [
  { month: "Sep", value: 42000 },
  { month: "Oct", value: 48000 },
  { month: "Nov", value: 46500 },
  { month: "Dec", value: 53000 },
  { month: "Jan", value: 58500 },
  { month: "Feb", value: 63200 },
];

const SAMPLE_FINDINGS = [
  "Acceptance lifted +1.4pts MoM on 3DS-routing changes — $12.4k incremental margin.",
  "Two legacy processor fees expired; renegotiation locked in $4.1k/mo run-rate savings.",
  "BNPL mix shift drove +6% AOV in the under-30 segment; expand to EU next sprint.",
];

const REPORT_MONTH = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

function Delta({ value }) {
  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[#0b8a3e] text-xs font-medium" data-testid="delta-up">
        <TrendingUp className="h-3 w-3" /> +{value.toFixed(1)}%
      </span>
    );
  }
  if (value < 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[#c0392b] text-xs font-medium" data-testid="delta-down">
        <TrendingDown className="h-3 w-3" /> {value.toFixed(1)}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-muted-foreground text-xs font-medium" data-testid="delta-flat">
      <Minus className="h-3 w-3" /> 0.0%
    </span>
  );
}

export default function MonthlyPerformanceReport({ metrics }) {
  const { isSample, trend, mrr, mom, marginPct, findings } = useMemo(() => {
    const real = metrics?.monthly?.length >= 2;
    if (!real) {
      const last = SAMPLE_MONTHLY[SAMPLE_MONTHLY.length - 1].value;
      const prev = SAMPLE_MONTHLY[SAMPLE_MONTHLY.length - 2].value;
      return {
        isSample: true,
        trend: SAMPLE_MONTHLY,
        mrr: last,
        mom: ((last - prev) / prev) * 100,
        marginPct: 38.2,
        findings: SAMPLE_FINDINGS,
      };
    }
    const t = metrics.monthly.slice(-6);
    const last = t[t.length - 1]?.value || 0;
    const prev = t[t.length - 2]?.value || 0;
    return {
      isSample: false,
      trend: t,
      mrr: last,
      mom: prev > 0 ? ((last - prev) / prev) * 100 : 0,
      marginPct: metrics.margin_pct || 0,
      findings: [
        `${metrics.engagements_count || 0} active engagement${(metrics.engagements_count || 0) === 1 ? "" : "s"} this month, ${fmtUSD(metrics.total_invested)} cumulative invest.`,
        `Net margin uplift of ${fmtUSD(metrics.margin)} delivered — ${metrics.margin_pct}% of invest.`,
        `Average value per engagement: ${fmtUSD(metrics.value_per_engagement)}. Your engagement lead will walk you through the drivers on the next monthly review.`,
      ],
    };
  }, [metrics]);

  return (
    <div
      className="card-sharp p-6 lg:p-7 border-[#0b1e5a]/15 bg-gradient-to-br from-[#0b1e5a]/[0.025] via-white to-[#ac62ea]/[0.04] relative overflow-hidden"
      data-testid="monthly-performance-report"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0b1e5a] flex items-center justify-center">
            <FileText className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="label-mono">Monthly performance report</div>
            <div className="font-display text-xl mt-0.5">{REPORT_MONTH}</div>
          </div>
        </div>
        {isSample && (
          <span
            className="px-2.5 py-1 text-[10px] uppercase tracking-widest rounded-full bg-[#ac62ea]/10 text-[#ac62ea] border border-[#ac62ea]/30"
            data-testid="report-sample-tag"
          >
            Sample preview
          </span>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <div>
            <div className="label-mono">Executive summary</div>
            <ul className="mt-3 space-y-2.5">
              {findings.map((f, i) => {
                const key = `${i}-${f.slice(0, 32)}`;
                return (
                  <li key={key} className="flex gap-3 text-sm text-foreground/85" data-testid={`report-finding-${i}`}>
                    <span className="text-[#ac62ea] font-semibold flex-shrink-0">0{i + 1}</span>
                    <span>{f}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div data-testid="report-kpi-mrr">
              <div className="label-mono">Monthly value</div>
              <div className="font-display text-lg mt-1">{fmtUSD(mrr)}</div>
              <Delta value={mom} />
            </div>
            <div data-testid="report-kpi-margin">
              <div className="label-mono">Margin</div>
              <div className="font-display text-lg mt-1">{marginPct.toFixed(1)}%</div>
              <span className="text-xs text-muted-foreground">of invest</span>
            </div>
            <div data-testid="report-kpi-trend">
              <div className="label-mono">Trend</div>
              <div className="font-display text-lg mt-1 text-[#0b8a3e]">↗</div>
              <span className="text-xs text-muted-foreground">last 6 mo</span>
            </div>
          </div>
          <div className="h-20 -mx-1" data-testid="report-sparkline">
            <ResponsiveContainer>
              <AreaChart data={trend} margin={SPARKLINE_MARGIN}>
                <defs>
                  <linearGradient id="sparklineGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ac62ea" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#ac62ea" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#0b1e5a"
                  strokeWidth={2}
                  fill="url(#sparklineGrad)"
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          {isSample
            ? "This is a preview of the monthly report Studio Retainer clients receive. Live data appears here once your first engagement is scoped."
            : `Reviewed by SideB Consulting Studio · ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
        </div>
        <Button
          asChild={!isSample}
          disabled={isSample}
          size="sm"
          variant="outline"
          className="rounded-full"
          data-testid="report-download-btn"
          title={isSample ? "Available once your first engagement is scoped" : "Download PDF"}
        >
          {isSample ? (
            <span>Download PDF <ArrowUpRight className="ml-1 h-3 w-3" /></span>
          ) : (
            <a href="#" onClick={(e) => e.preventDefault()}>
              Download PDF <ArrowUpRight className="ml-1 h-3 w-3" />
            </a>
          )}
        </Button>
      </div>
    </div>
  );
}
